/**
 * Pure chart-derivation helpers for the Status Overview. Free of React /
 * Redux side effects so it can be reused from memoized selectors and
 * property-based tests.
 */

import { FullAsinWithAudits } from "../../../from-server/asin-central/model";
import {
  AggregationResult,
  BucketResult,
} from "../../../from-server/asin-central/search";

export type StatusColor =
  | "success"
  | "success-light"
  | "error"
  | "info"
  | "grey";

export type BarKey =
  | "overall"
  | "lvs"
  | "hattrick"
  | "gtm"
  | "dgl"
  | "rl"
  | "instock"
  | "ox"
  | "cs";

/** A single colored region of a stacked bar (e.g. "LVS Failed"). */
export interface StatusSegment {
  readonly attributeKey: string;
  readonly statusValue: string;
  readonly label: string;
  readonly color: StatusColor;
  readonly count: number;
  /** Whole-number share in [0, 100]; sums to exactly 100 (or 0 if empty). */
  readonly percentage: number;
}

export interface StackedStatusBarConfig {
  readonly key: BarKey;
  readonly attributeKey: string;
  readonly title: string;
  readonly segments: readonly StatusSegment[];
  readonly total: number;
}

/**
 * Static declaration of one bar: which attribute to inspect, which
 * buckets to partition values into, and how to render each bucket. The
 * final bucket must be a `NO_DATA` catch-all so every ASIN lands
 * somewhere.
 */
export interface StatusAttributeSpec {
  readonly key: BarKey;
  readonly title: string;
  /**
   * DACS attribute used for aggregation. Must be `SortableField.KEYWORD`
   * in `AsinAttributeDefinitionDao`, otherwise DACS rejects the
   * aggregation request.
   */
  readonly attributeKey: string;
  /**
   * Chip shape written on segment click.
   *
   * `validationStatus` variant: the chip lives on the compound
   * `validationStatus` field with a value of `${prefix}_${statusValue}`
   * (e.g. `LVS_PASSED`). Used by per-pipeline bars where DACS stores
   * results in the compound list.
   *
   * `direct` variant: the chip lives directly on `attributeKey` with
   * the raw `statusValue` (e.g. `validationOverallStatus = PASSED`).
   * Used by bars whose backing attribute stores a single scalar value
   * per document.
   */
  readonly filterChip:
    | { readonly kind: "validationStatus"; readonly prefix: string }
    | { readonly kind: "direct" };
  readonly buckets: readonly StatusAttributeBucket[];
}

export interface StatusAttributeBucket {
  readonly statusValue: string;
  readonly label: string;
  readonly color: StatusColor;
  readonly matcher: (value: string | undefined) => boolean;
}

/** Attribute key used for chart-click filter chips (compound values). */
export const VALIDATION_STATUS_ATTRIBUTE_KEY = "validationStatus";

/**
 * Sentinel `statusValue` for the residual "coverage gap" bucket. The
 * bar renders this segment as a static visual — it isn't interactive
 * because per-pipeline attributes (LVS, Hattrick, etc.) leave the
 * field absent when the check hasn't run rather than writing a
 * literal `"NO_DATA"` value, so a drill-down chip wouldn't match
 * anything on those bars.
 */
export const NO_DATA_STATUS_VALUE = "NO_DATA";

// Bucket matchers. Accept both the lowercase Smithy wire values DACS
// stores (`pass`, `pass_exception`, `fail`) and the uppercase spellings
// the mock payload uses. Unknown / missing values fall through to the
// final NO_DATA catch-all.

const PASSED_STRICT_VALUES: ReadonlySet<string> = new Set([
  "PASSED",
  "PASS",
  "pass",
]);

const PASSED_WITH_EXCEPTIONS_VALUES: ReadonlySet<string> = new Set([
  "PASSED_WITH_EXCEPTIONS",
  "PASS_EXCEPTION",
  "pass_exception",
  "pass_exception_one_time",
]);

const FAILED_VALUES: ReadonlySet<string> = new Set(["FAILED", "FAIL", "fail"]);

const NOT_APPLICABLE_VALUES: ReadonlySet<string> = new Set([
  "NOT_APPLICABLE",
  "not_applicable",
]);

const matchesPassedStrict = (v: string | undefined): boolean =>
  v !== undefined && PASSED_STRICT_VALUES.has(v);

const matchesPassedWithExceptions = (v: string | undefined): boolean =>
  v !== undefined && PASSED_WITH_EXCEPTIONS_VALUES.has(v);

// Used by bars without a dedicated exceptions bucket so exception ASINs
// don't collapse to NO_DATA.
const matchesPassedInclusive = (v: string | undefined): boolean =>
  matchesPassedStrict(v) || matchesPassedWithExceptions(v);

const matchesFailed = (v: string | undefined): boolean =>
  v !== undefined && FAILED_VALUES.has(v);

const matchesNotApplicable = (v: string | undefined): boolean =>
  v !== undefined && NOT_APPLICABLE_VALUES.has(v);

const matchesNoData = (_v: string | undefined): boolean => true;

// Bucket builders. LVS and Hattrick get the four-bucket variant with a
// dedicated PASSED_WITH_EXCEPTIONS segment; the rest use the three-bucket
// layout. NO_DATA is always last so residual absorption stays deterministic.

const PASSED_BUCKET: StatusAttributeBucket = {
  statusValue: "PASSED",
  label: "Passed",
  color: "success",
  matcher: matchesPassedStrict,
};

const PASSED_INCLUSIVE_BUCKET: StatusAttributeBucket = {
  statusValue: "PASSED",
  label: "Passed",
  color: "success",
  matcher: matchesPassedInclusive,
};

const PASSED_WITH_EXCEPTIONS_BUCKET: StatusAttributeBucket = {
  statusValue: "PASSED_WITH_EXCEPTIONS",
  label: "Passed (w/ Exceptions)",
  color: "success-light",
  matcher: matchesPassedWithExceptions,
};

const FAILED_BUCKET: StatusAttributeBucket = {
  statusValue: "FAILED",
  label: "Failed",
  color: "error",
  matcher: matchesFailed,
};

const NOT_APPLICABLE_BUCKET: StatusAttributeBucket = {
  statusValue: "NOT_APPLICABLE",
  label: "Not Applicable",
  color: "info",
  matcher: matchesNotApplicable,
};

const NO_DATA_BUCKET: StatusAttributeBucket = {
  statusValue: "NO_DATA",
  label: "No Data",
  color: "grey",
  matcher: matchesNoData,
};

// Overall Validation Status: DACS reduces the eight per-pipeline
// results into a single PASSED / FAILED / NO_DATA scalar at ingest
// today, with no NOT_APPLICABLE rollup. Extending that reducer is a
// separate DACS change tracked outside this spec.
const buildOverallStatusBuckets = (): readonly StatusAttributeBucket[] => [
  PASSED_INCLUSIVE_BUCKET,
  FAILED_BUCKET,
  NO_DATA_BUCKET,
];

// Per-pipeline manual bars: DACS emits pass / fail / not_applicable,
// so NOT_APPLICABLE lands in its own bucket ahead of NO_DATA. The
// segment renders at width 0 and stays non-interactive on pipelines
// whose backend never emits the value.
const buildPerPipelineBuckets = (): readonly StatusAttributeBucket[] => [
  PASSED_INCLUSIVE_BUCKET,
  FAILED_BUCKET,
  NOT_APPLICABLE_BUCKET,
  NO_DATA_BUCKET,
];

// LVS and Hattrick additionally carry pass_exception, so the passed
// segment splits into strict-pass and pass-with-exceptions before
// FAILED, NOT_APPLICABLE, and NO_DATA.
const buildPerPipelineBucketsWithExceptions =
  (): readonly StatusAttributeBucket[] => [
    PASSED_BUCKET,
    PASSED_WITH_EXCEPTIONS_BUCKET,
    FAILED_BUCKET,
    NOT_APPLICABLE_BUCKET,
    NO_DATA_BUCKET,
  ];

// DACS-aggregatable attributes only: each `attributeKey` must be marked
// `SortableField.KEYWORD` in `AsinAttributeDefinitionDao` or DACS will
// reject the aggregation request.
export const STATUS_ATTRIBUTE_SPECS: readonly StatusAttributeSpec[] = [
  {
    key: "overall",
    title: "Validation Status",
    attributeKey: "validationOverallStatus",
    filterChip: { kind: "direct" },
    buckets: buildOverallStatusBuckets(),
  },
  {
    key: "lvs",
    title: "LVS Status",
    attributeKey: "validationOverallResultLVS",
    filterChip: { kind: "validationStatus", prefix: "LVS" },
    buckets: buildPerPipelineBucketsWithExceptions(),
  },
  {
    key: "hattrick",
    title: "Hattrick Status",
    attributeKey: "validationOverallResultHattrick",
    filterChip: { kind: "validationStatus", prefix: "HATTRICK" },
    buckets: buildPerPipelineBucketsWithExceptions(),
  },
  {
    key: "gtm",
    title: "GTM Status",
    attributeKey: "validationOverallResultManualGTM",
    filterChip: { kind: "validationStatus", prefix: "MANUAL_GTM" },
    buckets: buildPerPipelineBuckets(),
  },
  {
    key: "dgl",
    title: "DGL Status",
    attributeKey: "validationOverallResultManualDGL",
    filterChip: { kind: "validationStatus", prefix: "MANUAL_DGL" },
    buckets: buildPerPipelineBuckets(),
  },
  {
    key: "rl",
    title: "RL Status",
    attributeKey: "validationOverallResultManualRL",
    filterChip: { kind: "validationStatus", prefix: "MANUAL_RL" },
    buckets: buildPerPipelineBuckets(),
  },
  {
    key: "instock",
    title: "Instock Status",
    attributeKey: "validationOverallResultManualInstock",
    filterChip: { kind: "validationStatus", prefix: "MANUAL_INSTOCK" },
    buckets: buildPerPipelineBuckets(),
  },
  {
    key: "ox",
    title: "OX Status",
    attributeKey: "validationOverallResultManualOX",
    filterChip: { kind: "validationStatus", prefix: "MANUAL_OX" },
    buckets: buildPerPipelineBuckets(),
  },
  {
    key: "cs",
    title: "CS Status",
    attributeKey: "validationOverallResultManualCS",
    filterChip: { kind: "validationStatus", prefix: "MANUAL_CS" },
    buckets: buildPerPipelineBuckets(),
  },
];

/**
 * `(spec, statusValue)` → the FilterBar chip shape for a segment click
 * on that bar. Returns `undefined` if the spec's `filterChip` kind is
 * not recognized (future-proofing).
 */
export function filterChipForSegment(
  spec: StatusAttributeSpec,
  statusValue: string
): { propertyKey: string; value: string } | undefined {
  if (spec.filterChip.kind === "validationStatus") {
    return {
      propertyKey: VALIDATION_STATUS_ATTRIBUTE_KEY,
      value: `${spec.filterChip.prefix}_${statusValue}`,
    };
  }
  if (spec.filterChip.kind === "direct") {
    return {
      propertyKey: spec.attributeKey,
      value: statusValue,
    };
  }
  return undefined;
}

/**
 * Inverse of {@link filterChipForSegment}: given a chip's
 * `(propertyKey, value)`, return which bar it drives and the
 * `statusValue` to highlight, or `undefined` if the chip doesn't
 * belong to any Status Overview bar.
 */
export function parseFilterChip(
  propertyKey: string,
  value: string
): { attributeKey: string; statusValue: string } | undefined {
  if (propertyKey === VALIDATION_STATUS_ATTRIBUTE_KEY) {
    for (const spec of STATUS_ATTRIBUTE_SPECS) {
      if (spec.filterChip.kind !== "validationStatus") {
        continue;
      }
      const prefixWithSep = `${spec.filterChip.prefix}_`;
      if (value.startsWith(prefixWithSep)) {
        return {
          attributeKey: spec.attributeKey,
          statusValue: value.slice(prefixWithSep.length),
        };
      }
    }
    return undefined;
  }
  const directSpec = STATUS_ATTRIBUTE_SPECS.find(
    (s) => s.filterChip.kind === "direct" && s.attributeKey === propertyKey
  );
  if (directSpec !== undefined) {
    return {
      attributeKey: directSpec.attributeKey,
      statusValue: value,
    };
  }
  return undefined;
}

/**
 * `true` iff a chip's `propertyKey` could originate from any Status
 * Overview bar. Used to strip status-driven chips when computing the
 * aggregation baseline.
 */
export function isBarChipPropertyKey(propertyKey: string): boolean {
  if (propertyKey === VALIDATION_STATUS_ATTRIBUTE_KEY) {
    return true;
  }
  return STATUS_ATTRIBUTE_SPECS.some(
    (s) => s.filterChip.kind === "direct" && s.attributeKey === propertyKey
  );
}

// String-index read so specs can target fields that may not exist yet
// on the client-side `FullAsinWithAudits` type.
function readAttribute(
  asin: FullAsinWithAudits,
  attributeKey: string
): string | undefined {
  const raw = (asin as unknown as Record<string, unknown>)[attributeKey];
  return typeof raw === "string" ? raw : undefined;
}

function countBuckets(
  spec: StatusAttributeSpec,
  asins: readonly FullAsinWithAudits[]
): readonly number[] {
  const counts = new Array<number>(spec.buckets.length).fill(0);
  for (const asin of asins) {
    const value = readAttribute(asin, spec.attributeKey);
    for (let i = 0; i < spec.buckets.length; i++) {
      if (spec.buckets[i].matcher(value)) {
        counts[i] = counts[i] + 1;
        break;
      }
    }
  }
  return counts;
}

// Any bucket with count > 0 gets at least this many percent so the
// segment stays visible on the bar even when its share rounds to 0%.
const MIN_VISIBLE_PERCENT = 1;

/**
 * Floor each count to a whole-number percent, then redistribute so
 * segments sum to exactly 100 (or 0 when empty). Any non-zero-count
 * bucket that floored to 0 is bumped to `MIN_VISIBLE_PERCENT` so it
 * stays visible. Positive residual goes to the largest-count bucket
 * (earlier bucket wins ties). Debt from bumps is repaid by the
 * largest-percent bucket one point at a time.
 */
function distributePercentages(counts: readonly number[]): readonly number[] {
  const total = counts.reduce((sum, c) => sum + c, 0);
  if (total === 0) {
    return counts.map(() => 0);
  }

  const result = counts.map((c) => {
    const floored = Math.floor((c * 100) / total);
    return c > 0 && floored === 0 ? MIN_VISIBLE_PERCENT : floored;
  });

  const currentSum = result.reduce((sum, p) => sum + p, 0);
  let residual = 100 - currentSum;

  if (residual > 0) {
    let absorbIndex = -1;
    let absorbCount = 0;
    for (let i = 0; i < counts.length; i++) {
      if (counts[i] > 0 && counts[i] > absorbCount) {
        absorbCount = counts[i];
        absorbIndex = i;
      }
    }
    if (absorbIndex >= 0) {
      result[absorbIndex] = result[absorbIndex] + residual;
    }
    return result;
  }

  while (residual < 0) {
    let donorIndex = -1;
    let donorPercent = MIN_VISIBLE_PERCENT;
    for (let i = 0; i < result.length; i++) {
      if (result[i] > donorPercent) {
        donorPercent = result[i];
        donorIndex = i;
      }
    }
    if (donorIndex < 0) {
      break;
    }
    result[donorIndex] = result[donorIndex] - 1;
    residual = residual + 1;
  }

  return result;
}

export function buildStackedStatusBarConfig(
  spec: StatusAttributeSpec,
  asins: readonly FullAsinWithAudits[]
): StackedStatusBarConfig {
  const counts = countBuckets(spec, asins);
  const percentages = distributePercentages(counts);

  const segments: readonly StatusSegment[] = spec.buckets.map((bucket, i) => ({
    attributeKey: spec.attributeKey,
    statusValue: bucket.statusValue,
    label: bucket.label,
    color: bucket.color,
    count: counts[i],
    percentage: percentages[i],
  }));

  return {
    key: spec.key,
    attributeKey: spec.attributeKey,
    title: spec.title,
    segments,
    total: asins.length,
  };
}

export function buildAllStackedStatusBarConfigs(
  asins: readonly FullAsinWithAudits[]
): readonly StackedStatusBarConfig[] {
  return STATUS_ATTRIBUTE_SPECS.map((spec) =>
    buildStackedStatusBarConfig(spec, asins)
  );
}

// Aggregation-driven variants. Percentages are relative to the total
// match count, so documents that lack the attribute count toward the
// `NO_DATA` segment: a match set of 100 documents with 20 missing the
// field renders as 20% No Data.
//
// Every spec's final bucket must be the `NO_DATA` catch-all — the
// residual absorption below relies on that invariant.

function bucketCountsFromAggregation(
  spec: StatusAttributeSpec,
  buckets: readonly BucketResult[] | undefined
): readonly number[] {
  const counts = new Array<number>(spec.buckets.length).fill(0);

  for (const bucket of buckets ?? []) {
    const rawKey = bucket.key ?? "";
    const count = bucket.documentCount ?? 0;
    for (let i = 0; i < spec.buckets.length; i++) {
      if (spec.buckets[i].matcher(rawKey)) {
        counts[i] = counts[i] + count;
        break;
      }
    }
  }

  return counts;
}

export function buildStackedStatusBarConfigFromAggregation(
  spec: StatusAttributeSpec,
  buckets: readonly BucketResult[] | undefined,
  totalCount: number
): StackedStatusBarConfig {
  const bucketCounts = bucketCountsFromAggregation(spec, buckets);

  // Fold docs missing the attribute into the trailing NO_DATA bucket
  // so the bar always sums to `totalCount`.
  const bucketSum = bucketCounts.reduce((sum, c) => sum + c, 0);
  const residual = Math.max(0, totalCount - bucketSum);
  const noDataIndex = spec.buckets.length - 1;
  const counts = bucketCounts.map((c, i) =>
    i === noDataIndex ? c + residual : c
  );

  const percentages = distributePercentages(counts);

  const segments: readonly StatusSegment[] = spec.buckets.map((bucket, i) => ({
    attributeKey: spec.attributeKey,
    statusValue: bucket.statusValue,
    label: bucket.label,
    color: bucket.color,
    count: counts[i],
    percentage: percentages[i],
  }));

  return {
    key: spec.key,
    attributeKey: spec.attributeKey,
    title: spec.title,
    segments,
    total: totalCount,
  };
}

export function buildAllStackedStatusBarConfigsFromAggregation(
  aggregationResults: AggregationResult | undefined,
  totalCount: number
): readonly StackedStatusBarConfig[] {
  return STATUS_ATTRIBUTE_SPECS.map((spec) =>
    buildStackedStatusBarConfigFromAggregation(
      spec,
      aggregationResults?.[spec.attributeKey],
      totalCount
    )
  );
}
